import API_CONFIG from '../config/config';
import { authManager } from './authManager';

const empreendimentosCache = {
  MRV: {
    Flex: null,
    Carteira: null,
  },
  SENSIA: {
    Flex: null,
    Carteira: null,
  },
};

const unidadesCache = {
  MRV: {
    Flex: {},
    Carteira: {},
  },
  SENSIA: {
    Flex: {},
    Carteira: {},
  },
};

// Cache para resultados do cálculo de rating
const ratingPrePacCache = {};

let authTokenCache = null;

// Função para limpar cache de empreendimentos
export const clearEmpreendimentosCache = (isMRV = null, tipo = null) => {
  if (isMRV === null) {
    Object.keys(empreendimentosCache).forEach(marca => {
      Object.keys(empreendimentosCache[marca]).forEach(tipoCache => {
        empreendimentosCache[marca][tipoCache] = null;
      });
    });
    Object.keys(unidadesCache).forEach(marca => {
      Object.keys(unidadesCache[marca]).forEach(tipoCache => {
        unidadesCache[marca][tipoCache] = {};
      });
    });
  } else {
    // Limpar cache de uma marca específica
    const companyKey = isMRV ? 'MRV' : 'SENSIA';
    if (tipo) {
      empreendimentosCache[companyKey][tipo] = null;
      unidadesCache[companyKey][tipo] = {};
    } else {
      Object.keys(empreendimentosCache[companyKey]).forEach(tipoCache => {
        empreendimentosCache[companyKey][tipoCache] = null;
      });
      Object.keys(unidadesCache[companyKey]).forEach(tipoCache => {
        unidadesCache[companyKey][tipoCache] = {};
      });
    }
  }
};

// Obter token da URL, do cache, ou do globalThis._iframeAuthToken
const getAuthToken = () => {
  // Se o token foi atualizado via postMessage, priorize ele
  if (globalThis._iframeAuthToken) {
    authTokenCache = globalThis._iframeAuthToken;
    return globalThis._iframeAuthToken;
  }
  if (authTokenCache) return authTokenCache;

  const urlParams = new URLSearchParams(globalThis.location.search);
  const token = urlParams.get('token');

  if (token) {
    authTokenCache = token;
    return token;
  }

  return null;
};

// Permite limpar o cache do token ao receber novo via postMessage
globalThis._clearAuthTokenCache = () => {
  authTokenCache = null;
};

// Adicionar headers de autenticação
const addAuthHeaders = (headers = {}) => {
  const token = getAuthToken();
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
};

// Tratar resposta HTTP
const handleResponse = async response => {
  if (response.status === 401) {
    // Limpar token e mostrar erro
    authTokenCache = null;
    authManager.setAuthError('Token inválido ou expirado');
    throw new Error('Não autorizado');
  }
  if (response.status === 503) {
    throw new Error(
      ' Nossos servidores estão sobrecarregados ou em manutenção neste momento. Esta pausa é temporária e estaremos de volta rapidamente!'
    );
  }

  if (!response.ok) {
    const errorData = await response.json();
    // Tentar obter a mensagem de erro de diferentes propriedades
    const errorMessage = errorData.message || errorData.Detalhe || 'Erro desconhecido';
    throw new Error(errorMessage);
  }

  return await response.json();
};

// Função para buscar empreendimentos
export const fetchEmpreendimentos = async (isMRV, tipo = 'Carteira') => {
  // Limpar erro ao tentar novamente
  authManager.clearAuthError();
  const companyKey = isMRV ? 'MRV' : 'SENSIA';

  if (empreendimentosCache[companyKey][tipo]) return empreendimentosCache[companyKey][tipo];

  try {
    const headers = addAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(
      `${API_CONFIG.urlListarEmpreendimentos}?marca=${companyKey}&tipo=${tipo}`,
      { headers }
    );
    const data = await handleResponse(response);

    // Armazenar no cache específico da empresa e tipo
    empreendimentosCache[companyKey][tipo] = data;
    return data;
  } catch (error) {
    console.error('Erro ao obter empreendimentos:', error);
    throw error;
  }
};

// Função para buscar unidades (assumindo que exista)
export const fetchUnidades = async (empreendimentoId, isMRV, tipo = 'Carteira') => {
  authManager.clearAuthError();
  const companyKey = isMRV ? 'MRV' : 'SENSIA';

  // Usar cache específico para a empresa, tipo e empreendimento
  if (unidadesCache[companyKey][tipo][empreendimentoId])
    return unidadesCache[companyKey][tipo][empreendimentoId];

  try {
    const headers = addAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(
      `${API_CONFIG.urlListarUnidades}?empreendimentoId=${empreendimentoId}&marca=${companyKey}&tipo=${tipo}`,
      { headers }
    );
    const data = await handleResponse(response);

    // Armazenar no cache específico da empresa, tipo e empreendimento
    unidadesCache[companyKey][tipo][empreendimentoId] = data;
    return data;
  } catch (error) {
    console.error('Erro ao obter unidades:', error);
    throw error;
  }
};

export const calcularRatingPrePac = async ratingRequest => {
  authManager.clearAuthError();

  // Criar chave única para o cache baseada nos parâmetros
  const cacheKey = `${ratingRequest.ClienteIdade}-${ratingRequest.Estado}-${ratingRequest.RendaTotal}`;
  console.log(cacheKey);
  // Verificar se já existe no cache
  if (ratingPrePacCache[cacheKey]) {
    console.log('Usando rating pre-pac do cache');
    return ratingPrePacCache[cacheKey];
  }

  try {
    const headers = addAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(API_CONFIG.urlRatingPrepac, {
      method: 'POST',
      headers,
      body: JSON.stringify(ratingRequest),
    });

    const result = await handleResponse(response);

    // Armazenar resultado no cache
    ratingPrePacCache[cacheKey] = result;
    return result;
  } catch (error) {
    console.error('Erro ao calcular rating pre-pac:', error);
    throw error;
  }
};

// Função para simular proposta
export const simularProposta = async dadosProposta => {
  authManager.clearAuthError();

  const headers = addAuthHeaders({ 'Content-Type': 'application/json' });
  const response = await fetch(API_CONFIG.urlSimularProposta, {
    method: 'POST',
    headers,
    body: JSON.stringify(dadosProposta),
  });

  return await handleResponse(response);
};

// Função para inicializar proposta
export const inicializarProposta = async dados => {
  authManager.clearAuthError();

  try {
    const authToken = getAuthToken();

    const headers = {
      'Content-Type': 'application/json',
      Authorization: authToken ? `Bearer ${authToken}` : '',
    };

    const payload = {
      ...dados,
    };

    const response = await fetch(API_CONFIG.urlInicializarPropostaFlex, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    const result = await handleResponse(response);
    console.log('[inicializarProposta] Resposta recebida:', result);
    return result;
  } catch (error) {
    console.error('[inicializarProposta] Erro:', error);
    throw error;
  }
};

// Função para simular proposta flex
export const simularPropostaFlex = async dados => {
  authManager.clearAuthError();

  try {
    const authToken = getAuthToken();

    const headers = {
      'Content-Type': 'application/json',
      Authorization: authToken ? `Bearer ${authToken}` : '',
    };

    const response = await fetch(API_CONFIG.urlSimularPropostaFlex, {
      method: 'POST',
      headers,
      body: JSON.stringify(dados),
    });

    const result = await handleResponse(response);
    console.log('[simularPropostaFlex] Resposta recebida:', result);
    return result;
  } catch (error) {
    console.error('[simularPropostaFlex] Erro:', error);
    throw error;
  }
};
